import apiClient from './client'

// ── Availability types (GET /scheduler/my-availability) ─────────────────────
export interface AvailabilitySlot {
  start: string       // ISO, localized to the interviewer's timezone
  end: string
  start_utc: string
  end_utc: string
  is_booked: boolean
}

export interface AvailabilityDay {
  date: string        // YYYY-MM-DD
  slots: AvailabilitySlot[]
}

export interface MyAvailability {
  timezone: string
  connected: boolean  // true once the native scheduler has config/rules set up
  days: AvailabilityDay[]
}

// One change request the interviewer raised against a booking.
// Interviewers can NEVER self-reschedule/cancel — they raise a request that an
// admin resolves. Matches scheduler.ChangeRequestItem.
export interface ChangeRequestItem {
  id: number
  booking_id: number
  type: string          // "cancel" | "reschedule"
  reason: string | null
  status: string        // "pending" | "approved" | "rejected"
  admin_notes: string | null
  scheduled_start_utc: string | null
  booking_status: string | null
  created_at: string
  updated_at: string
}

export const interviewerApi = {
  getProfile: () =>
    apiClient.get('/interviewer/profile').then((r) => r.data),

  updateProfile: (data: Record<string, unknown>) =>
    apiClient.put('/interviewer/profile', data).then((r) => r.data),

  getSchedule: () =>
    apiClient.get('/interviewer/schedule').then((r) => r.data),

  updateAvailability: (slots: Record<string, unknown>[]) =>
    apiClient
      .put('/interviewer/schedule/availability', { slots })
      .then((r) => r.data),

  toggleAvailability: (is_available: boolean) =>
    apiClient
      .put('/interviewer/schedule/availability/toggle', { is_available })
      .then((r) => r.data),

  getMySchedule: () =>
    apiClient
      .get('/scheduler/my-schedule')
      .then((r) => r.data.data ?? r.data),

  // GET /scheduler/my-availability?start=YYYY-MM-DD&days=7
  // Served by the native scheduler. The authenticated interviewer's own open
  // bookable slots (working hours minus booked minus buffer, overlap-blocked,
  // lead-time enforced), localized to their tz. `connected` is true when native
  // config/rules exist, so the Available Slots tab keeps working.
  getMyAvailability: (start: string, days = 7) =>
    apiClient
      .get('/scheduler/my-availability', { params: { start, days } })
      .then((r) => (r.data.data ?? r.data) as MyAvailability),

  getClients: () =>
    apiClient.get('/interviewer/clients').then((r) => r.data.data ?? r.data),

  getClientQuestionSets: (clientUserId: number) =>
    apiClient.get(`/question-sets/client/${clientUserId}`).then((r) => r.data.data ?? r.data),

  getClientSessions: (clientId: number) =>
    apiClient.get(`/interviewer/sessions/${clientId}`).then((r) => r.data.data ?? r.data),

  getSessions: (params?: Record<string, unknown>) =>
    apiClient.get('/sessions', { params }).then((r) => r.data.data ?? r.data),

  getSession: (id: number) =>
    apiClient.get(`/sessions/${id}`).then((r) => r.data),

  getBooking: (id: number) =>
    apiClient.get(`/bookings/${id}`).then((r) => r.data.data ?? r.data),

  getStats: () =>
    apiClient.get('/stats').then((r) => r.data),

  getDashboard: () =>
    apiClient.get('/interviewer/dashboard').then((r) => r.data.data ?? r.data),

  cancelBooking: (bookingId: number, reason?: string) =>
    apiClient
      .put(`/interviewer/bookings/${bookingId}/cancel`, { reason })
      .then((r) => r.data),

  // ── Change requests (interviewer cannot self-reschedule/cancel) ─────────────
  // Raises a request an admin must approve/reject. bookingId is a CalendlyBooking
  // id (the `booking_id` field on getSessions() rows / /scheduler/my-schedule).
  requestBookingChange: (bookingId: number, data: { type: 'cancel' | 'reschedule'; reason?: string }) =>
    apiClient
      .post(`/scheduler/bookings/${bookingId}/change-request`, data)
      .then((r) => (r.data.data ?? r.data) as ChangeRequestItem),

  getMyChangeRequests: () =>
    apiClient.get('/scheduler/change-requests').then((r) => {
      const d = r.data.data ?? r.data
      return (d?.change_requests ?? d ?? []) as ChangeRequestItem[]
    }),

  getClientPrepInfo: (clientUserId: number, bookingId?: number) =>
    apiClient
      .get(`/interviewer/clients/${clientUserId}`, {
        params: bookingId ? { booking_id: bookingId } : undefined,
      })
      .then((r) => r.data.data ?? r.data),

  getJdSignedUrl: (key: string) =>
    apiClient
      .get(`/scheduler/job-description/signed-url`, { params: { key } })
      .then((r) => (r.data.data ?? r.data) as { signed_url: string }),

  generateQuestionsForClient: (
    clientUserId: number,
    data: { num_questions?: number; job_description?: string | null },
  ) =>
    apiClient
      .post(`/interviewer/clients/${clientUserId}/generate-questions`, data)
      .then((r) => r.data.data ?? r.data),

  saveQuestionSet: (data: { session_id: number; questions: object[]; is_finalized?: boolean }) =>
    apiClient
      .post('/question-sets', data)
      .then((r) => r.data.data ?? r.data),

  deleteQuestionSet: (id: number) =>
    apiClient.delete(`/question-sets/${id}`).then((r) => r.data),

  generateAnswerForQuestion: (data: { question: string; client_user_id: number }) =>
    apiClient
      .post('/question-sets/generate-answer', data)
      .then((r) => (r.data.data ?? r.data) as { expected_answer: string }),

  getRecordings: () =>
    apiClient.get('/interviewer/recordings').then((r) => r.data.data ?? r.data),

  getRecordingTranscript: (recordingId: number) =>
    apiClient.get(`/interviewer/recordings/${recordingId}/transcript`, {
      responseType: 'text',
    }).then((r) => r.data as string),

  getBookingTranscript: (bookingId: number) =>
    apiClient.get(`/interviewer/bookings/${bookingId}/transcript`, {
      responseType: 'text',
    }).then((r) => r.data as string),

  getQuestionTemplates: (target_role?: string) =>
    apiClient.get('/questions', { params: { per_page: 100, ...(target_role ? { target_role } : {}) } }).then((r) => r.data.data ?? r.data),

  getQuestionTypes: () =>
    apiClient.get('/question-types').then((r) => r.data.data ?? r.data),

  getNotificationPrefs: () =>
    apiClient.get('/interviewer/notification-preferences').then((r) => r.data),

  // ── Zoom OAuth ────────────────────────────────────────────────────────────
  getZoomStatus: () =>
    apiClient
      .get<{ data: { is_connected: boolean; zoom_user_email: string | null; connected_at: string | null } }>('/zoom/oauth/status')
      .then((r) => r.data.data ?? r.data),

  getZoomAuthUrl: () =>
    apiClient
      .get<{ data: { authorization_url: string } }>('/zoom/oauth/authorize')
      .then((r) => r.data.data ?? r.data),

  connectZoom: (code: string, state: string) =>
    apiClient
      .get('/zoom/oauth/callback', { params: { code, state } })
      .then((r) => r.data),

  disconnectZoom: () =>
    apiClient.delete('/zoom/oauth/disconnect').then((r) => r.data),

  updateNotificationPrefs: (prefs: {
    email_session_reminder: boolean
    email_evaluation_reminder: boolean
    email_new_client_assignment: boolean
  }) =>
    apiClient
      .put('/interviewer/notification-preferences', prefs)
      .then((r) => r.data),
}
